<script setup lang="ts">
  const { t, tmResolved } = useResolvedI18n()
  const localePath = useLocalePath()

  useHead({ title: () => t('about.title') })

  const structureItems = computed(() => tmResolved<Array<{ label: string; desc: string }>>('about.ability.structure'))
  const targetItems = computed(() => tmResolved<Array<{ label: string; desc: string }>>('about.ability.targets'))
</script>

<template>
  <!-- Page Banner -->
  <section
    class="page-banner"
    style="
      background-image: url('https://www.sanyau.edu.cn/_upload/article/images/d5/1b/4291df2943a5835305e3c154685c/05d057a3-7eb9-4511-9ac9-8553fe1d8faf.jpg');
    "
  >
    <h2>{{ t('about.banner') }}</h2>
    <div class="breadcrumb">
      <NuxtLink :to="localePath('/')">{{ t('common.home') }}</NuxtLink>
      &gt; {{ t('about.breadcrumb') }}
    </div>
  </section>

  <!-- Content -->
  <section class="content-section">
    <div class="container">
      <div class="content-wrapper">
        <div class="content-main">
          <!-- 学院简介 -->
          <div class="content-card" id="intro">
            <h3>{{ t('about.intro.title') }}</h3>
            <p>{{ t('about.intro.body') }}</p>

            <div class="highlight-box">
              <h3>{{ t('about.intro.capabilitiesTitle') }}</h3>
              <p>
                <strong>{{ t('about.intro.capability1Label') }}</strong>
                {{ t('about.intro.capability1') }}
              </p>
              <p>
                <strong>{{ t('about.intro.capability2Label') }}</strong>
                {{ t('about.intro.capability2') }}
              </p>
              <p>
                <strong>{{ t('about.intro.capability3Label') }}</strong>
                {{ t('about.intro.capability3') }}
              </p>
            </div>
          </div>

          <!-- 办学理念 -->
          <div class="content-card" id="philosophy">
            <h3>{{ t('about.philosophy.title') }}</h3>
            <p>{{ t('about.philosophy.intro') }}</p>

            <div class="highlight-box">
              <h4>{{ t('about.philosophy.aiTitle') }}</h4>
              <p>{{ t('about.philosophy.aiBody') }}</p>
            </div>

            <div class="highlight-box">
              <h4>{{ t('about.philosophy.projectTitle') }}</h4>
              <p>{{ t('about.philosophy.projectBody') }}</p>
            </div>

            <div class="highlight-box">
              <h4>{{ t('about.philosophy.industryTitle') }}</h4>
              <p>{{ t('about.philosophy.industryBody') }}</p>
            </div>

            <p>{{ t('about.philosophy.closing') }}</p>
          </div>

          <!-- 三校园体系 -->
          <div class="content-card" id="campus">
            <h3>{{ t('about.campus.title') }}</h3>
            <p>{{ t('about.campus.intro') }}</p>

            <div class="campus-grid" style="margin-top: 20px">
              <div class="campus-card">
                <div class="campus-card-img" style="background-image: url('/images/campus-1.jpg')"></div>
                <div class="campus-card-body">
                  <h3>{{ t('about.campus.base.title') }}</h3>
                  <p>{{ t('about.campus.base.desc') }}</p>
                </div>
              </div>
              <div class="campus-card">
                <div class="campus-card-img" style="background-image: url('/images/campus-2.jpg')"></div>
                <div class="campus-card-body">
                  <h3>{{ t('about.campus.mobile.title') }}</h3>
                  <p>{{ t('about.campus.mobile.desc') }}</p>
                </div>
              </div>
              <div class="campus-card">
                <div class="campus-card-img" style="background-image: url('/images/campus-3.jpg')"></div>
                <div class="campus-card-body">
                  <h3>{{ t('about.campus.virtual.title') }}</h3>
                  <p>{{ t('about.campus.virtual.desc') }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 能力体系 -->
          <div class="content-card" id="ability">
            <h3>{{ t('about.ability.title') }}</h3>
            <p>{{ t('about.ability.intro') }}</p>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('about.ability.structureTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in structureItems" :key="i">
                <strong>{{ item.label }}</strong>
                {{ item.desc }}
              </li>
            </ul>

            <h4 style="font-size: 17px; color: var(--theme-blue); margin: 20px 0 10px">
              {{ t('about.ability.targetTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in targetItems" :key="i">
                <strong>{{ item.label }}</strong>
                {{ item.desc }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Sidebar -->
        <aside class="content-sidebar">
          <div class="sidebar-nav">
            <h4>{{ t('about.sidebar.navTitle') }}</h4>
            <ul>
              <li>
                <a href="#intro">{{ t('about.sidebar.navIntro') }}</a>
              </li>
              <li>
                <a href="#philosophy">{{ t('about.sidebar.navPhilosophy') }}</a>
              </li>
              <li>
                <a href="#campus">{{ t('about.sidebar.navCampus') }}</a>
              </li>
              <li>
                <a href="#ability">{{ t('about.sidebar.navAbility') }}</a>
              </li>
            </ul>
          </div>
          <div class="sidebar-nav">
            <h4>{{ t('common.contactInfo') }}</h4>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 8px">
              {{ t('footer.addressValue') }}
            </p>
            <p style="font-size: 14px; color: var(--text-secondary); margin-bottom: 8px">
              {{ t('footer.phoneValue') }}
            </p>
            <p style="font-size: 14px; color: var(--text-secondary)">{{ t('footer.emailValue') }}</p>
          </div>
          <div class="sidebar-nav">
            <h4>{{ t('common.quickLinks') }}</h4>
            <ul>
              <li>
                <NuxtLink :to="localePath('/programs')">{{ t('about.sidebar.linkPrograms') }}</NuxtLink>
              </li>
              <li>
                <NuxtLink :to="localePath('/education')">{{ t('about.sidebar.linkEducation') }}</NuxtLink>
              </li>
              <li>
                <NuxtLink :to="localePath('/admission')">{{ t('about.sidebar.linkAdmission') }}</NuxtLink>
              </li>
              <li>
                <NuxtLink :to="localePath('/contact')">{{ t('about.sidebar.linkContact') }}</NuxtLink>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>
